/**
 * Employer login page — sets an employer_token cookie via /api/employer/login.
 * On success, routes to /discover (verified) or /employer/pending (not).
 */
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Briefcase, LogIn, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmployerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || "/discover";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/employer/login`,
        { email, password }, { withCredentials: true });
      if (r.data.status === "verified") {
        navigate(returnTo, { replace: true });
      } else {
        navigate("/employer/pending", { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen py-10 px-4"
         style={{ background: "linear-gradient(180deg, #FFFBEB 0%, #FEF3C7 100%)" }}>
      <div className="max-w-md mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full"
               style={{ background: "#0F1B2D", color: "#FFC145" }}>
            <Briefcase size={14} />
            <span className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ fontWeight: 700 }}>
              Employer sign in
            </span>
          </div>
          <h1 className="font-display uppercase"
              style={{ fontSize: "clamp(1.4rem, 2.6vw, 1.9rem)", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
            Welcome back, recruiter
          </h1>
        </div>
        <form onSubmit={submit}
              className="rounded-md p-5 space-y-3"
              style={{ background: "white", border: "2px solid #0F1B2D", boxShadow: "4px 4px 0 0 #FFC145" }}
              data-testid="employer-login-form">
          <label className="block">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                 style={{ color: "#475569", fontWeight: 700 }}>· Work email</div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                   className="w-full rounded px-3 py-2 text-[13px]"
                   style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                   data-testid="employer-login-email" />
          </label>
          <label className="block">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1"
                 style={{ color: "#475569", fontWeight: 700 }}>· Password</div>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                   className="w-full rounded px-3 py-2 text-[13px]"
                   style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                   data-testid="employer-login-password" />
          </label>
          {error && (
            <div className="text-[12px] rounded px-3 py-2"
                 style={{ background: "#FEE2E2", color: "#B91C1C", border: "1px solid #FCA5A5" }}
                 data-testid="employer-login-error">{error}</div>
          )}
          <button type="submit" disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 font-display uppercase text-[13px] tracking-widest"
                  style={{
                    background: busy ? "#94A3B8" : "#FF6A00", color: "white",
                    border: "2px solid #0F1B2D",
                    boxShadow: "3px 3px 0 0 #0F1B2D",
                    fontWeight: 700, cursor: busy ? "wait" : "pointer",
                  }}
                  data-testid="employer-login-submit">
            {busy ? "Signing in…" : (<><LogIn size={14} /> Sign in <ArrowRight size={14} /></>)}
          </button>
          <div className="text-center mt-1 text-[11px]" style={{ color: "#64748B" }}>
            Don&apos;t have an account?{" "}
            <Link to="/employer/signup" className="underline" style={{ color: "#0F1B2D", fontWeight: 600 }}
                  data-testid="employer-login-goto-signup">
              Sign up
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
